import { useThreeContext } from "@/components/common/three-context/three-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  DynamicMapObject,
  loadDynamicMapSource,
} from "@/features/dynamic-overview-map/dynamic-map-source";
import {
  Features,
  selectGetFeatureValueIfEnabled,
  selectHasFeature,
} from "@/store/features/features-slice";
import { RootState, store } from "@/store/store";
import {
  createSignedUrlForIElementUri,
  createSignedUrlPanoFetcher,
  loadLodFloorPlan,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import {
  PriorityTaskQueue,
  SignedUrl,
  assert,
  removeExtension,
  robustFetchJson,
} from "@faro-lotv/foundation";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import {
  CadModelTreeDescription,
  GUID,
  IElement,
  IElementBase,
  IElementDynamicTiledMap,
  IElementGaussianSplats,
  IElementGenericImgSheet,
  IElementGenericModel3d,
  IElementGenericPointCloudStream,
  IElementImg2d,
  IElementImg360,
  IElementImgSheet,
  IElementImgSheetTiled,
  IElementModel3dStream,
  IElementType,
  Model3dStreamDescription,
  firstVersionSubMeshesInProd,
  isCadModelTreeDescription,
  isIElementImgSheetTiled,
  isIElementModel3dStream,
  isIElementPointCloudStreamWebShare,
  isModel3dStreamURLs,
  isPotreeURLs,
  isValid,
} from "@faro-lotv/ielement-types";
import {
  AdaptivePointsMaterial,
  CadModel,
  Disposable,
  FloorPlanBackgroundTransparency,
  FloorPlanMaterial,
  ICadModel,
  LodFloorPlan,
  LodPano,
  LodPointCloud,
  LodTree,
  LodTreeFetcher,
  ModelAboutToLoad,
  ModelChunkMetadata,
  ModelChunkMetadataSigned,
  TextureLoader,
  TypedEvent,
  WSInstance,
  createCadModel,
  createOutOfCoreStreamCadModel,
  createPotree,
  createStreamCadModel,
  loadGltf,
  outOfCoreStreamCadModelSettings,
} from "@faro-lotv/lotv";
import {
  PointCloudApiClient,
  ProjectApi,
  makeIElementUriSigner,
  makeInitialIElementSignatures,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { SplatMesh } from "@faro-lotv/spark-gaussian-splats";
import { createPanoLodTree, getPanoOverviewUrl } from "@faro-lotv/spatial-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import {
  Color,
  Group,
  LinearMipMapLinearFilter,
  Mesh,
  Object3D,
  PlaneGeometry,
} from "three";
import { useCadVisibilityDistance } from "./hooks/use-cad-visibility-minimum-distance";
import { useChangeCache } from "./hooks/use-change-cache";
import { useForceRender } from "./hooks/use-force-render";
import { useModelAboutToLoad } from "./hooks/use-model-about-to-load";
import {
  CacheEntry,
  type ObjectCacheAcquirer,
  type ObjectCacheAcquirers,
} from "./object-cache-entry";
import { Photo2DObject } from "./photo-2d";
import { selectDefaultCadMaxByteToLoad } from "./store/cad/cad-slice";
import { useAppSelector } from "./store/store-hooks";

export const pointCloudQueue = new PriorityTaskQueue<number, void>({
  maxParallelTasks: 20,
});
const initialLoadPointCloudQueue = new PriorityTaskQueue<
  GUID,
  PointCloudObject
>({
  maxParallelTasks: 20,
});

/** The object this cache is holding */
export type CachedObject<Cache> =
  Cache extends ObjectCache<IElementBase, infer T> ? T : never;

/** Function to get a token to authenticate for API calls */
type TokenProvider = () => Promise<string>;

type LoaderFunctionParams = {
  /** A function to get a token to talk to the api */
  tokenProvider?: TokenProvider;
  /** A function to report that GPU is about to freeze to load 3D model */
  modelLoadingCallback?: ModelAboutToLoad;
  /**
   * Function returning the maximum number of bytes to load in memory for 3D model.
   * We are passing a function because actual value is not yet known when starting the download.
   */
  getModelMaxByteToLoad(): number;
  /** An instance of the ProjectApi to sign download URLs with */
  projectApi: ProjectApi;
  /** Client for the PointCloud API, needed to render dynamic overview map tiles */
  pointCloudApiClient?: PointCloudApiClient;
};

/** Function to load an iElement in the cache */
type LoaderFunction<Element, ObjectType> = (
  iElement: Element,
  params: LoaderFunctionParams,
) => Promise<ObjectType>;

/**
 * Very short lifespan of five seconds before disposing, for very volatile objects such pano images.
 * This is done mainly because pano images have expensive depth data, to deallocate as soon as possible,
 * and because they have many tiles, which can create problems especially on Apple mobile devices.
 */
const VERY_SHORT_LIFESPAN = 5000;

/** Short lifespan of 1 minute (ms before disposing) to use to cache short lived objects (Eg. Floorplans) */
const SHORT_LIFESPAN = 60000;

/** Long lifespan of 10 minutes (ms before disposing) to use for long lived objects (Eg. PointClouds, CadModels) */
const LONG_LIFESPAN = 600000;

/**
 * A cache for 3d objects
 */
export class ObjectCache<
  CachedElement extends IElementBase = IElementBase,
  Type extends Object3D = Object3D,
> {
  /** The cache entries */
  #cache = new Map<GUID, CacheEntry<Type>>();
  /** The function used to load an entry in this cache */
  #loaderFunction: LoaderFunction<CachedElement, Type>;
  /** The lifespan for the object in this cache */
  lifespan: number;

  /**
   * Event called, when an existing entry for an element is updated. E.g. when it is re-created for updated data.
   * Does not emit for new entries.
   */
  onEntryUpdated = new TypedEvent<{
    element: CachedElement;
    entry: CacheEntry<Type>;
  }>();

  /**
   * Create a new object cache
   *
   * @param loaderFunction The function used to load objects in this cache
   * @param lifespan The lifespan of objects in this cache
   */
  constructor(
    loaderFunction: LoaderFunction<CachedElement, Type>,
    lifespan = SHORT_LIFESPAN,
  ) {
    this.#loaderFunction = loaderFunction;
    this.lifespan = lifespan;
  }

  /**
   * Peek for an item in the cache without suspending
   *
   * @param key The key for the item
   * @returns The item or undefined if not available
   */
  peek(key: GUID): Type | undefined {
    return this.#cache.get(key)?.response;
  }

  /**
   * Access a cache entry if it exists in the cache
   *
   * @param key to search in the cache
   * @returns the cache entry with all item metadata and state
   */
  entry(key: GUID): Readonly<CacheEntry<Type>> | undefined {
    return this.#cache.get(key);
  }

  /**
   * Start loading an object in the cache
   *
   * @param iElement The descriptor of the object to load
   * @param params Optional parameters for the loading
   * @returns The cache entry
   */
  preload(
    iElement: CachedElement,
    params: LoaderFunctionParams,
  ): CacheEntry<Type> {
    const key = iElement.id;
    let entry = this.#cache.get(key);
    if (entry) {
      return entry;
    }
    entry = new CacheEntry(
      this.#loaderFunction(iElement, params),
      key,
      this.lifespan,
    );
    entry.disposed.on(() => this.#cache.delete(key));
    this.#cache.set(key, entry);
    return entry;
  }

  /**
   * Reloads a cache entry. Use e.g. if the resource behind the cache entry was updated and the object should re-initialize.
   * The entry will be returned immediately. If you need to wait for the re-load to complete, you can await the entry's promise.
   *
   * @param iElement The element to reload the cache for.
   * @param params The options passed to the loader function.
   * @returns Reloaded entry, or undefined if no entry to reload is in the cache.
   */
  reload(
    iElement: CachedElement,
    params: LoaderFunctionParams,
  ): CacheEntry<Type> | undefined {
    const key = iElement.id;
    const existingEntry = this.#cache.get(key);

    if (!existingEntry) return;

    const newEntry = new CacheEntry(
      this.#loaderFunction(iElement, params),
      key,
      this.lifespan,
    );

    existingEntry.moveAcquirersTo(newEntry);

    existingEntry.dispose();

    newEntry.disposed.on(() => this.#cache.delete(key));

    this.#cache.set(key, newEntry);
    this.onEntryUpdated.emit({ element: iElement, entry: newEntry });

    return newEntry;
  }

  /**
   * Get an object or start loading it and suspend
   *
   * @param iElement The descriptor of the object to query
   * @param params Optional parameters for the loading
   * @returns The object or suspend
   */
  getOrLoad(iElement: CachedElement, params: LoaderFunctionParams): Type {
    const entry = this.#cache.get(iElement.id);
    if (entry) {
      return entry.getOrSuspend();
    }
    return this.preload(iElement, params).getOrSuspend();
  }

  /**
   *
   * @param id ID of iElement being loaded
   * @returns Whether the loading promise has been settled, regardless of fulfillment of rejection.
   */
  isPromiseSettled(id: string): boolean {
    const entry = this.#cache.get(id);
    if (!entry) return true;
    return entry.promiseSettled;
  }

  /**
   * Adds an acquirer to a given object.
   *
   * @param iElement The descriptor of the object to add an acquirer to
   * @param acquirer The acquirer to add to the object
   */
  acquire(iElement: CachedElement, acquirer: ObjectCacheAcquirer): void {
    const cacheElement = this.#cache.get(iElement.id);
    if (!cacheElement) {
      throw Error(
        `${acquirer.label} is requesting to acquire an ${iElement.id} that is not in cache`,
      );
    }
    cacheElement.acquire(acquirer);
  }

  /**
   * Releases an acquirer from a given object.
   *
   * @param iElement The descriptor of the object to remove an acquirer from
   * @param acquirer The acquirer to remove from the object
   */
  release(iElement: CachedElement, acquirer: ObjectCacheAcquirer): void {
    const cacheElement = this.#cache.get(iElement.id);
    if (!cacheElement) {
      throw Error(
        `Requesting to release an acquirer of an ${iElement.id} that is not in cache`,
      );
    }
    cacheElement.release(acquirer);
  }

  /**
   * @returns the number of objects in the cache
   */
  get size(): number {
    return this.#cache.size;
  }

  /** @returns a map of cache entry IDs to their current acquirers */
  get entriesAndAcquirers(): Map<GUID, ObjectCacheAcquirers> {
    const result = new Map<GUID, ObjectCacheAcquirers>();
    for (const [key, entry] of this.#cache.entries()) {
      if (entry.isReferenced) {
        result.set(key, entry.acquirers);
      }
    }
    return result;
  }
}

/**
 * In the app all object 3d are paired with their project descriptor
 */
export type IElementObject<
  ThreeObject extends Object3D,
  Desc extends IElement,
> = ThreeObject & { iElement: Desc };

/** The type of a Panorama image 3d object */
export type PanoObject = IElementObject<LodPano, IElementImg360>;

/**
 * An async function to load a pano object
 *
 * @param iElement The descriptor for the pano to load
 * @returns A promise that will resolve when the PanoObject is ready
 */
async function loadPano(
  iElement: IElementImg360,
  { projectApi }: LoaderFunctionParams,
): Promise<PanoObject> {
  const fetcher = createSignedUrlPanoFetcher(projectApi, iElement);

  const imageTree = createPanoLodTree(iElement, {
    fetcher,
  });
  const url = await getPanoOverviewUrl(iElement);
  const texture = await fetcher(url).promise;
  return Object.assign(new LodPano(imageTree, texture), { iElement });
}

/** The type of a sheet image object */
export type SheetObject =
  | IElementObject<LodFloorPlan, IElementImgSheetTiled>
  | IElementObject<Mesh<PlaneGeometry, FloorPlanMaterial>, IElementImgSheet>;

/**
 * Async function to load a dynamic overview map
 *
 * @param iElement The descriptor of the map to load
 * @param params parameters for the loading function
 * @returns a promise that will resolve to the loaded map
 */
async function loadDynamicMap(
  iElement: IElementDynamicTiledMap,
  params: LoaderFunctionParams,
): Promise<DynamicMapObject> {
  const source = await loadDynamicMapSource(iElement, params);
  return Object.assign(source, { iElement });
}

/**
 * Async function to load a SheetObject
 *
 * @param iElement The descriptor of the sheet to load
 * @param params Optional parameters for the loading function
 * @returns a promise that will resolve to the loaded SheetObject
 */
async function loadSheet(
  iElement: IElementGenericImgSheet,
  { projectApi }: Pick<LoaderFunctionParams, "projectApi">,
): Promise<SheetObject> {
  if (isIElementImgSheetTiled(iElement)) {
    const floor = await loadLodFloorPlan(iElement, projectApi);

    // A tiled floor plan contains several levels of tiles, and all tiles from one tiled floor plan
    // must be rendered before another tiled floor plan can be rendered. So each tiled floor plan
    // need to have a unique render order, so all tiles of the same plan can be rendered together.
    // The number of tiled floor plans loaded can be used as the render order.
    const cache = getCache(iElement);
    floor.renderOrder = cache ? cache.size : 0;
    return Object.assign(floor, { iElement });
  }

  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    iElement,
  ).requestSignedUrl();

  const texture = await new TextureLoader().load(signedUrl).promise;
  // If the sheet is not LOD, mipmaps are needed to have good rendering of images with
  // crisp lines at any zoom level.
  texture.generateMipmaps = true;
  texture.minFilter = LinearMipMapLinearFilter;
  const sizeX = iElement.size?.x ?? 1;
  const sizeZ = iElement.size?.z ?? 1;
  const mesh = new Mesh(
    new PlaneGeometry(sizeX, sizeZ),
    new FloorPlanMaterial(texture, FloorPlanBackgroundTransparency.No),
  );
  return Object.assign(mesh, { iElement });
}

/** The type of a mesh object */
export type MeshObject = IElementObject<Group | Mesh, IElementGenericModel3d>;

/** The type of a CAD model */
export type CadModelObject = IElementObject<ICadModel, IElementGenericModel3d>;

/**
 * Async function to get the Model3dStreamDescription associated with a given IElementModel3dStream.
 *
 * @param iElement The descriptor of the IElementModel3dStream associated with the Model3dStreamDescription
 * @param params Optional parameters for the loading function
 * @returns The Model3dStreamDescription (with throw exception in case of failure)
 */
export async function loadModel3dStreamDescription(
  iElement: IElementModel3dStream,
  { projectApi }: Pick<LoaderFunctionParams, "projectApi">,
): Promise<Model3dStreamDescriptionSigned> {
  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    iElement,
  ).requestSignedUrl();

  const res = await fetch(signedUrl);
  if (!res.ok) {
    throw new Error("Error while fetching Model 3D description");
  }
  const urls = await res.json();
  if (!isModel3dStreamURLs(urls)) {
    throw new Error("Invalid Model3dStream url");
  }

  return createSignedModel3dStreamDescription(urls, projectApi, iElement.id);
}

/** Signed version of the Model3dStreamDescription */
export type Model3dStreamDescriptionSigned = {
  /** Optional URL to the glb file of the model. Was generated only until 2024-08-12. */
  meshGlbUrl?: SignedUrl | null;
  /** URL that contains the optional metadata of the model */
  metadataUrl: SignedUrl | null;
  /** URL that contains the optional model tree (this tag was introduced on October 5th = it might be missing in some projects) */
  modelTreeUrl: SignedUrl | null;
  /** URL that contains the optional zipped model tree (this tag was introduced on December 12th, 2025) */
  modelTreeZipUrl?: SignedUrl | null;
  /** URL that contains the metadata of list sub meshes. Was not generated prior 2024-06-14. */
  subMeshesUrl?: SignedUrl | null;
  /** Optional URL to the grid sub meshes of the model. */
  gridSubMeshesUrl?: SignedUrl | null;
};

async function createSignedModel3dStreamDescription(
  urls: Model3dStreamDescription,
  projectApi: ProjectApi,
  iElementId: GUID,
): Promise<Model3dStreamDescriptionSigned> {
  const signer = makeIElementUriSigner(projectApi, iElementId);

  const entries = Object.entries(urls).filter(
    (entry): entry is [string, string] => isValid(entry[1]),
  );

  const initialSignatures = await makeInitialIElementSignatures(
    projectApi,
    iElementId,
    entries.map(([, url]) => url),
  );

  const signedEntries = entries.map(([key, url], index) => [
    key,
    new SignedUrl(url, signer, initialSignatures[index]),
  ]);

  return Object.fromEntries(signedEntries);
}

/**
 * Async function to get the CAD model tree for a given IElementModel3dStream.
 *
 * @param iElement The descriptor of the IElementModel3dStream associated with the CAD
 * @param params Optional parameters for the loading function
 * @returns The CAD Model tree if any, or undefined if there is no model tree.
 */
export async function loadCadModelTree(
  iElement: IElementModel3dStream,
  params: Pick<LoaderFunctionParams, "projectApi">,
): Promise<CadModelTreeDescription | undefined> {
  const model3dStreamDescription = await loadModel3dStreamDescription(
    iElement,
    params,
  );

  if (!model3dStreamDescription.modelTreeUrl) {
    return;
  }

  // modelTreeUrl will either point to a unzipped json (legacy prior 2025-12-10),
  // or a brotli json that will be automatically decompressed by the browser thanks to the content-encoding header.
  const res = await fetch(
    await model3dStreamDescription.modelTreeUrl.requestSignedUrl(),
  );
  if (!res.ok) {
    throw new Error("Invalid modelTreeUrl uri");
  }
  const modelTree = await res.json();

  if (!isCadModelTreeDescription(modelTree)) {
    throw new Error("Invalid ModelTreeDescription url");
  }
  return modelTree;
}

/**
 * Async function to fetch the legacy IElementGenericModel3d's monolithic GLTF file from a remote URL and parse it
 * into a ThreeJS scene.
 *
 * @param iElement The descriptor of the model to load
 * @param params Optional parameters for the loading function
 * @returns An object with a parsed GLTF scene.
 */
async function loadMonolithicGLTF(
  iElement: IElementGenericModel3d,
  params: LoaderFunctionParams,
): Promise<Group> {
  // Validate source URL for GLTF.
  let signedUrl: SignedUrl;

  if (isIElementModel3dStream(iElement)) {
    const urls = await loadModel3dStreamDescription(iElement, params);
    if (!urls.meshGlbUrl) {
      // special case for missing GLB. We create an empty Group on purpose
      // and set userData.emptyMesh to indicate that it is not an error
      const ret = new Group();
      ret.userData.emptyMesh = true;
      return ret;
    }
    signedUrl = urls.meshGlbUrl;
  } else {
    signedUrl = createSignedUrlForIElementUri(params.projectApi, iElement);
  }

  try {
    return loadGltf(
      await signedUrl.requestSignedUrl(),
      params.modelLoadingCallback,
    );
  } catch {
    // If an error is caught here, an empty Group() is returned and the error is
    // stored inside the group's user data. Then, the caller function
    // can check whether result.userData.error is defined, and throw it in case.
    // Otherwise, errors throw here are lost.
    // After a lot of experiments, this is the only solution I could find
    // to report these errors.
    const error = new Error(
      `3D Model "${removeExtension(
        iElement.fileName ?? undefined,
      )}" is too big to render.`,
    );
    const ret = new Group();
    ret.userData.error = error;
    return ret;
  }
}

/**
 * Possible types for CadError.
 */
export enum CadErrorType {
  /** Missing mesh file, the conversion has failed */
  NoMeshFile = "NoMeshFile",
  /** Model has no geometry */
  EmptyModel = "EmptyModel",
  /** Model is too big to render */
  ModelTooBigToRender = "ModelTooBigToRender",
  /** Model is too big to render (was generated as mono mesh by legacy converter) */
  ModelTooBigToRenderLegacy = "ModelTooBigToRenderLegacy",
}

/**
 * Specialized Error type for loadCadModel.
 */
export class CadError extends Error {
  /**
   * @param type type of CadErrorType
   * @param cadFilename filename of the model triggering the error
   */
  constructor(cadFilename: string, type: CadErrorType) {
    super("CAD error");
    this.CadErrorType = type;
    this.cadFilename = cadFilename;
  }

  CadErrorType: CadErrorType;

  /**
   * Filename of the CAD.
   */
  cadFilename: string;
}

/**
 * @returns A promise of the requested StreamCadModel object, or undefined if the model is not streamable
 * @param iElement Descriptor of the Cad model to load
 * @param params Optional parameters for the loading function
 */
async function loadStreamCadModel(
  iElement: IElementModel3dStream,
  params: LoaderFunctionParams,
): Promise<CadModelObject | undefined> {
  const urls = await loadModel3dStreamDescription(iElement, params);

  const streamCadModelOutOfCore = selectHasFeature(
    Features.StreamCadModelOutOfCore,
  )(store.getState());

  if (streamCadModelOutOfCore) {
    const url = urls.gridSubMeshesUrl ?? urls.subMeshesUrl;
    if (!url) return;
    // The backend generates 3 static chunks for grid submeshes
    const staticChunks = 3;
    const streamCadModel = await createOutOfCoreStreamCadModel(
      url,
      params.getModelMaxByteToLoad,
      staticChunks,
      createCadModelChunksSigner(params.projectApi, iElement.id),
    );
    // Always load the static chunks, so the CAD model won't appear empty.
    await streamCadModel.loadStaticChunks();
    return Object.assign(streamCadModel, { iElement });
  }

  // No out of core support, load the static stream cad model
  if (urls.subMeshesUrl) {
    const streamCadModel = await createStreamCadModel(
      urls.subMeshesUrl,
      params.getModelMaxByteToLoad,
      createCadModelChunksSigner(params.projectApi, iElement.id),
    );
    // Always load the first chunk, so the CAD model won't appear empty.
    await streamCadModel.loadOneChunk();
    return Object.assign(streamCadModel, { iElement });
  }
}

/**
 * @returns a function that signs a set of CAD chunks efficiently in one request.
 * @param projectApi The projectAPI instance to use for signing
 * @param iElementId The ID of the IElement to sign chunks for
 */
function createCadModelChunksSigner(projectApi: ProjectApi, iElementId: GUID) {
  const signer = makeIElementUriSigner(projectApi, iElementId);

  return async (
    chunks: ModelChunkMetadata[],
  ): Promise<ModelChunkMetadataSigned[]> => {
    const initialSignatures = await makeInitialIElementSignatures(
      projectApi,
      iElementId,
      chunks.map((chunk) => chunk.GlbUrl),
    );

    return chunks.map((chunk, index) => ({
      ...chunk,
      GlbUrl: new SignedUrl(chunk.GlbUrl, signer, initialSignatures[index]),
    }));
  };
}

/**
 * Async function to fetch and load a Cad model
 *
 * @param iElement Descriptor of the Cad model to load
 * @param params Optional parameters for the loading
 * @returns A promise of the requested Cad model object
 */
async function loadCadModel(
  iElement: IElementModel3dStream,
  params: LoaderFunctionParams,
): Promise<CadModelObject> {
  const streamCadModel = await loadStreamCadModel(iElement, params);
  if (streamCadModel) return streamCadModel;

  // Fallback to the single CAD model GLB file
  const model = await loadMonolithicGLTF(iElement, params);
  if (model.userData.error) {
    throw model.userData.error;
  }

  const cadName = removeExtension(iElement.fileName ?? undefined);

  let ret: CadModel;

  try {
    // Creating the Cad Model with custom rendering data structures and materials.
    ret = createCadModel(model);
  } catch {
    // date when submeshes started to be generated in PROD environment
    const dateDeployment = new Date(firstVersionSubMeshesInProd);
    // date this model was converted
    const dateConversion = new Date(iElement.createdAt);

    // check if the mode was converted prior support for large streamed model
    const errorId =
      dateConversion < dateDeployment
        ? CadErrorType.ModelTooBigToRenderLegacy
        : CadErrorType.ModelTooBigToRender;

    // Handling errors
    throw new CadError(cadName ?? "3D Model", errorId);
  }

  // If the loaded CAD model is empty (e.g. contains only lines) the app should inform the user
  if (model.userData.emptyMesh) {
    throw new CadError(cadName ?? "3D Model", CadErrorType.NoMeshFile);
  } else if (ret.nodesCount() === 0) {
    throw new CadError(cadName ?? "3D Model", CadErrorType.EmptyModel);
  }

  return Object.assign(ret, { iElement });
}

/** The type of a point cloud object */
export type PointCloudObject = IElementObject<
  LodPointCloud,
  IElementGenericPointCloudStream
> & { scanIdsToIndex?: Record<string, number> };

/**
 * A throttled version of {@link loadPointCloud}, to avoid overloading the API
 * when loading many point clouds at the same time.
 *
 * @param iElement The iElement to load the point cloud stream of.
 * @param params Additional parameters for the loading function.
 * @returns The loaded point cloud.
 */
async function loadPointCloudThrottled(
  iElement: IElementGenericPointCloudStream,
  params: LoaderFunctionParams,
): Promise<PointCloudObject> {
  return await initialLoadPointCloudQueue.run(
    () => loadPointCloud(iElement, params),
    iElement.id,
  );
}

/**
 * An async function to load a PointCloudObject
 *
 * @param iElement The descriptor of the lod pointcloud to load
 * @param params Optional parameters for the loading
 * @returns A promise that will resolve when the PointCloudObject is ready
 */
async function loadPointCloud(
  iElement: IElementGenericPointCloudStream,
  params: LoaderFunctionParams,
): Promise<PointCloudObject> {
  let tree: LodTree;
  const scanIdsToIndex: Record<string, number> = {};

  const signedUrl = await createSignedUrlForIElementUri(
    params.projectApi,
    iElement,
  ).requestSignedUrl();

  /**
   * The material parameters have been chosen from testing multiple datasets.
   * They have been found as a good compromise between covering 'holes' when few nodes are loaded
   * and avoid exaggerated point splats for outliers
   */
  const material = new AdaptivePointsMaterial({ minSize: 2, maxSize: 6 });
  if (isIElementPointCloudStreamWebShare(iElement)) {
    const ws = new WSInstance(signedUrl, params.tokenProvider);
    tree = await ws.getKDTree(
      iElement.webShareProjectName,
      iElement.webShareCloudId,
      iElement.webShareEntityId,
      true,
    );
    material.size = 2;
    material.sizeAttenuation = false;
  } else {
    const urls = await robustFetchJson({
      input: signedUrl,
      validateJson: isPotreeURLs,
    });

    const [
      initialMetaDataSignature,
      initialHierarchySignature,
      initialOctreeSignature,
    ] = await makeInitialIElementSignatures(params.projectApi, iElement.id, [
      urls.metaDataUrl,
      urls.hierarchyUrl,
      urls.octreeURL,
    ]);
    const urlUpdater = makeIElementUriSigner(params.projectApi, iElement.id);

    // Assigning directly to the tree object make it impossible to access
    // PoTree specific metadata as it will decay the type to LodTree
    const potree = await createPotree({
      metadata: new SignedUrl(
        urls.metaDataUrl,
        urlUpdater,
        initialMetaDataSignature,
      ),
      hierarchy: new SignedUrl(
        urls.hierarchyUrl,
        urlUpdater,
        initialHierarchySignature,
      ),
      octree: new SignedUrl(urls.octreeURL, urlUpdater, initialOctreeSignature),
    });
    if (potree.monochrome) {
      material.vertexColors = false;
      material.color = new Color("white");
    }

    material.size = 0.8;
    material.sizeAttenuation = true;

    tree = potree;

    if (urls.sourceMapUrl) {
      const scanIds = await robustFetchJson({
        input: urls.sourceMapUrl,
        // TODO: add type-guard https://faro01.atlassian.net/browse/NRT-1325
        validateJson: (obj: unknown): obj is Record<number, string> => true,
      });

      for (const [key, value] of Object.entries(scanIds)) {
        scanIdsToIndex[value] = Number(key);
      }
    }
  }

  // Setting convenient point cloud rendering parameters
  tree.visibleNodesStrategy.targetPixelsPerPoint = 0.5;
  tree.visibleNodesStrategy.maxPointsInGpu = 10_000_000;
  // Share the same download queue for all point clouds
  // This allows to limit the number of parallel download across all point clouds
  const fetcher = new LodTreeFetcher(tree, pointCloudQueue);
  const pointCloud = new LodPointCloud(fetcher, material);
  // Setting raycasting parameters optimized for speed and precision
  pointCloud.raycasting = {
    ...pointCloud.raycasting,
    enabled: true,
    // A small threshold make sure the picked points are closer to the cursor while raycasting
    // but could create problem with sparse point cloud
    threshold: 0.01,
    pickingTree: {
      enabled: true,
      autoUpdate: true,
      maxDepth: 3,
    },
    // This option will force the raycasting to visit all loaded nodes of the LodPointCloud
    maxDepth: Number.MAX_SAFE_INTEGER,
    shouldReturnOnlyClosest: true,
    maxPickingTreesPerRaycast: 1,
  };
  // A single node worker can easily handle more than 1 download at a time
  // but starting too many download can overload the queue
  // 6 downloads with the default 4 workers tested as a nice compromise on multiple devices/networks
  pointCloud.lodTreeFetcher.maxNodesToDownloadAtOnce = 20;

  // Log every time a node failed, either due to network or processing issues.
  pointCloud.lodTreeFetcher.nodeFailed.on(({ reason }) =>
    console.error(reason),
  );

  pointCloud.preloadRootNode();
  return Object.assign(pointCloud, { iElement, scanIdsToIndex });
}

/** The type of a Gaussian splats object */
export type GaussianSplatsObject = IElementObject<
  SplatMesh,
  IElementGaussianSplats
>;

/**
 * Async function to load a Gaussian splats object
 *
 * @param iElement The descriptor of the Gaussian splats to load
 * @param params Optional parameters for the loading
 * @returns A promise that will resolve when the GaussianSplatsObject is ready
 */
async function loadGaussianSplats(
  iElement: IElementGaussianSplats,
  params: LoaderFunctionParams,
): Promise<GaussianSplatsObject> {
  const signedUrl = await createSignedUrlForIElementUri(
    params.projectApi,
    iElement,
  ).requestSignedUrl();

  const gaussianSplats = new SplatMesh({
    url: signedUrl,
    paged: new URL(signedUrl).pathname.endsWith(".rad"),
    extSplats: true,
  });

  return Object.assign(gaussianSplats, { iElement });
}

/** The type of a 2D photo cache object */
export type PhotoObject = IElementObject<Photo2DObject, IElementImg2d>;

// photo image size in meter
export const PHOTO_SIZE = 1;
/**
 * Async function to load a PhotoObject
 *
 * @param iElement The descriptor of the 2D photo to load
 * @param params Optional parameters for the loading function
 * @returns a promise that will resolve to the loaded PhotoObject
 */
async function loadPhoto(
  iElement: IElementImg2d,
  { projectApi }: Pick<LoaderFunctionParams, "projectApi">,
): Promise<PhotoObject> {
  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    iElement,
  ).requestSignedUrl();

  const texture = await new TextureLoader().load(signedUrl).promise;
  const maxPixelSize =
    iElement.pixelWidth > iElement.pixelHeight
      ? iElement.pixelWidth
      : iElement.pixelHeight;

  // set the plane geometry large side at 1 meter and the shorter side propositional to image width and height ratio
  return Object.assign(
    new Photo2DObject(
      new PlaneGeometry(
        (PHOTO_SIZE * iElement.pixelWidth) / maxPixelSize,
        (PHOTO_SIZE * iElement.pixelHeight) / maxPixelSize,
      ),
      new FloorPlanMaterial(texture, FloorPlanBackgroundTransparency.No),
    ),
    { iElement },
  );
}

/** All the supported iElements in our cache */
type SupportedElements =
  | IElementImg360
  | IElementImgSheet
  | IElementImgSheetTiled
  | IElementDynamicTiledMap
  | IElementModel3dStream
  | IElementGenericPointCloudStream
  | IElementGaussianSplats
  | IElementImg2d;

/** Map with metadata for each object type supported by the cache */
const OBJECT_CACHES = {
  [IElementType.img360]: new ObjectCache(loadPano, VERY_SHORT_LIFESPAN),
  [IElementType.imgSheet]: new ObjectCache(loadSheet),
  [IElementType.imgSheetTiled]: new ObjectCache(loadSheet),
  [IElementType.dynamicTiledMap]: new ObjectCache(loadDynamicMap),
  [IElementType.model3dStream]: new ObjectCache(loadCadModel, SHORT_LIFESPAN),
  [IElementType.pointCloudStreamWebShare]: new ObjectCache(
    loadPointCloudThrottled,
    SHORT_LIFESPAN,
  ),
  [IElementType.pointCloudStream]: new ObjectCache(
    loadPointCloudThrottled,
    SHORT_LIFESPAN,
  ),
  [IElementType.gaussianSplats]: new ObjectCache(
    loadGaussianSplats,
    LONG_LIFESPAN,
  ),
  [IElementType.img2d]: new ObjectCache(loadPhoto),
};

type IElementObjects = {
  [IElementType.img360]: PanoObject;
  [IElementType.imgSheet]: SheetObject;
  [IElementType.imgSheetTiled]: SheetObject;
  [IElementType.dynamicTiledMap]: DynamicMapObject;
  [IElementType.model3dStream]: CadModelObject;
  [IElementType.pointCloudStreamWebShare]: PointCloudObject;
  [IElementType.pointCloudStream]: PointCloudObject;
  [IElementType.gaussianSplats]: GaussianSplatsObject;
  [IElementType.img2d]: PhotoObject;
};

/**
 * @param iElement we want the cache for
 * @returns the cache instance that manages the iElement types passed as argument
 */
function getCache<T extends SupportedElements>(
  iElement: T | undefined,
): ObjectCache<T, IElementObjects[T["type"]]> | undefined {
  // Query object type metadata
  const cache = iElement ? OBJECT_CACHES[iElement.type] : undefined;
  if (!cache) {
    return;
  }

  // We know here the cache is the correct one, but typescript can't infer that
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return cache as unknown as ObjectCache<T, IElementObjects[T["type"]]>;
}

/** Helper type for the promise returned by getPromise() */
type MaybeCachePromiseOf<T extends SupportedElements> =
  | Promise<IElementObjects[T["type"]]>
  | undefined;

/**
 * @returns the Promise of an IElement in the cache if an entry exists
 * @param iElement the IElement to get the promise for
 */
function getPromise<T extends SupportedElements>(
  iElement: T | undefined,
): MaybeCachePromiseOf<T> {
  if (!iElement?.id) return;
  return getCache(iElement)?.entry(iElement.id)?.promise;
}

/**
 * Increment ref on mount, decrement on unmount
 *
 * @param cache The cache holding the element
 * @param iElement An element for which to handle the reference counter increments
 * @param acquirer The acquirer to add and remove from the element
 */
export function useCacheRef<T extends SupportedElements>(
  cache: ObjectCache<T, IElementObjects[T["type"]]> | undefined,
  iElement: T | undefined,
  acquirer: ObjectCacheAcquirer,
): void {
  useEffect(() => {
    if (cache && iElement) {
      cache.acquire(iElement, acquirer);
      return () => cache.release(iElement, acquirer);
    }
  }, [cache, iElement, acquirer]);
}

/**
 * Increment ref on mount, decrement on unmount
 *
 * @param caches The caches holding the elements
 * @param iElements An array of elements for which to handle the reference counter increments
 * @param acquirer The acquirer to add and remove from the elements
 */
function useCacheRefs<T extends SupportedElements>(
  caches:
    | Array<ObjectCache<T, IElementObjects[T["type"]]> | undefined>
    | undefined,
  iElements: Array<T | undefined> | undefined,
  acquirer: ObjectCacheAcquirer,
): void {
  useEffect(() => {
    if (!iElements || !caches) return;
    if (iElements.length !== caches.length) return;

    iElements.forEach(
      (el, index) => el && caches[index]?.acquire(el, acquirer),
    );
    return () => {
      iElements.map((el, index) => el && caches[index]?.release(el, acquirer));
    };
  }, [iElements, caches, acquirer]);
}

/** Will update the apparentSizeThreshold option for all CADs */
function useUpdateCadChunksSorting(): void {
  const streamCadModelSorting = useAppSelector(
    selectGetFeatureValueIfEnabled(Features.OrderCadChunkByScreenSize),
  );
  useMemo(() => {
    // Update the apparentSizeThreshold in global settings once on first stream
    outOfCoreStreamCadModelSettings.apparentSizeThreshold =
      streamCadModelSorting ? Number(streamCadModelSorting) : undefined;
  }, [streamCadModelSorting]);
}

/** Will update the visibility distance of active CAD */
function useUpdateCadVisibilityDistance(): void {
  const { actualCadVisibilityDistance } = useCadVisibilityDistance();
  useMemo(() => {
    // Update the minVisibleDistance of the streaming CAD model when the visibility distance changes OR when active CAD changes
    outOfCoreStreamCadModelSettings.minVisibleDistance =
      actualCadVisibilityDistance;
  }, [actualCadVisibilityDistance]);
}

/**
 * Creates a unique, constant object cache acquirer with the given label. This object
 * is unique per-instance of the hook. Two concurrent instances of the hook
 * will have different acquirers, even if the label is the same.
 *
 * @param label The label to apply to the acquirer. If label changes during this
 * hook's lifetime, only the first value counts.
 * @returns A unique object that will live as long as this hook is mounted
 */
export function useObjectCacheAcquirer(label: string): ObjectCacheAcquirer {
  const [acquirer] = useState<ObjectCacheAcquirer>({ label });
  return acquirer;
}

/**
 * Get an object from the cache, suspend if the object does not exist
 *
 * @param iElement The descriptor of the element
 * @param acquirerLabel Label of the acquirer to add and remove from the object
 * @returns The loaded object or suspend execution
 */
export function useCached3DObject<T extends SupportedElements>(
  iElement: T,
  acquirerLabel: string,
): IElementObjects[T["type"]] {
  const acquirer = useObjectCacheAcquirer(acquirerLabel);

  const { tokenProvider } = useAuthContext();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();

  const cache = getCache(iElement);
  if (!cache) {
    throw new Error(
      `${iElement.type} type is not supported by the object-cache`,
    );
  }

  useRenderOnCacheUpdateForElement(cache, iElement.id);
  useCacheRef(cache, iElement, acquirer);
  return cache.getOrLoad(iElement, {
    tokenProvider,
    getModelMaxByteToLoad: () => getModelMaxByteToLoad(store.getState()),
    projectApi,
    pointCloudApiClient,
  });
}

/**
 * Use a cached 3d object if the iElement exists, null if the ielement is not valid or failed to load
 *
 * @param iElement The iElement we want to get the 3d object of
 * @param acquirerLabel Label of the acquirer to add and remove from the object
 * @param abortAndReportOnLoadingError true to report loading error with handleErrorWithDialog; this will probably kill the app
 * @returns The correct 3d object, or null if the iElement is invalid
 */
export function useCached3DObjectIfExists<T extends SupportedElements>(
  iElement: T | undefined | null,
  acquirerLabel: string,
  abortAndReportOnLoadingError = true,
): IElementObjects[T["type"]] | null {
  const iElements = useMemo(
    () => (iElement ? [iElement] : undefined),
    [iElement],
  );

  const objects = useCached3DObjectsIfExists(
    iElements,
    acquirerLabel,
    abortAndReportOnLoadingError,
  );
  return objects[0] ?? null;
}

/**
 * Use cached 3d objects if the iElements exists
 *
 * @param iElements The list of iElements we want to get the 3d object of
 * @param acquirerLabel Label of the acquirer to add and remove from the cache entries
 * @param abortAndReportOnLoadingError true to report loading error with handleErrorWithDialog; this will probably kill the app
 * @returns The list valid 3d objects associated with input iElements. The iElement not valid or
 * failed to load won't have an entry in the result.
 */
export function useCached3DObjectsIfExists<T extends SupportedElements>(
  iElements: T[] | undefined | null,
  acquirerLabel: string,
  abortAndReportOnLoadingError = true,
): Array<IElementObjects[T["type"]]> {
  const acquirer = useObjectCacheAcquirer(acquirerLabel);

  const { tokenProvider } = useAuthContext();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();

  useUpdateCadVisibilityDistance();
  useUpdateCadChunksSorting();

  const { handleErrorWithDialog } = useErrorHandlers();

  const { iElementsWithCache, caches } = useElementsWithCache(
    iElements,
    acquirer,
  );

  const getOneCache = useCallback(
    (iElement: T, cache: ObjectCache<T, IElementObjects[T["type"]]>) => {
      try {
        return cache.getOrLoad(iElement, {
          tokenProvider,
          getModelMaxByteToLoad: () => getModelMaxByteToLoad(store.getState()),
          projectApi,
          pointCloudApiClient,
        });
      } catch (reason) {
        // Throwing a promise is not an error but it's needed to suspend the application
        if (reason instanceof Promise && !cache.isPromiseSettled(iElement.id)) {
          throw reason;
        } else if (reason instanceof Error) {
          if (abortAndReportOnLoadingError) {
            handleErrorWithDialog({
              title: getErrorLoadingMessage(iElement),
              error: reason.message,
            });
          }
        }
        return null;
      }
    },
    [
      abortAndReportOnLoadingError,
      handleErrorWithDialog,
      projectApi,
      pointCloudApiClient,
      tokenProvider,
    ],
  );

  const loadedObjects = useMemo(
    () =>
      iElementsWithCache
        .map((iElement, index) => getOneCache(iElement, caches[index]))
        .filter(
          (object): object is IElementObjects[T["type"]] => object !== null,
        ),
    [iElementsWithCache, caches, getOneCache],
  );

  return loadedObjects;
}

/**
 * @returns the 3d object of an ielement if it's already in the cache
 * @param iElement to query the relative 3d object for
 *
 * it will not increase the cache reference or extend the lifetime of the object in the cache
 */
export function getWeakRefToCachedObject<T extends SupportedElements>(
  iElement: T | undefined | null,
): IElementObjects[T["type"]] | undefined {
  if (!iElement) return;

  const cache = getCache(iElement);
  return cache?.peek(iElement.id);
}

/**
 * Get an array of objects from the cache, if any of them are not there return null and start preloading
 *
 * @param iElements The descriptor of the elements
 * @param acquirerLabel Label of the acquirer to add and remove from the cache entries
 * @returns The array of objects, entries are null if object is not loaded yet
 */
export function useCached3DObjectsIfReady<T extends SupportedElements>(
  iElements: T[] | undefined,
  acquirerLabel: string,
): Array<IElementObjects[T["type"]]> {
  const acquirer = useObjectCacheAcquirer(acquirerLabel);

  const { tokenProvider } = useAuthContext();
  const modelLoadingCallback = useModelAboutToLoad();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();
  const hasChanged = useChangeCache();

  const { iElementsWithCache, caches } = useElementsWithCache(
    iElements,
    acquirer,
  );

  // Try to load the elements from the cache.
  //
  // This MUST run synchronously during render (not in a useEffect): the
  // `useCacheRefs` hook inside `useElementsWithCache` runs in its own effect
  // and calls `cache.addRef(iElement)`, which throws if the entry hasn't been
  // inserted into the cache yet. `preload` is idempotent (returns the existing
  // entry if any), so calling it from `useMemo` is safe.
  const cacheEntries = useMemo(
    () =>
      iElementsWithCache.map((iElement, index) =>
        caches[index]?.preload(iElement, {
          tokenProvider,
          modelLoadingCallback,
          getModelMaxByteToLoad: () => getModelMaxByteToLoad(store.getState()),
          projectApi,
          pointCloudApiClient,
        }),
      ),
    // We only want to (re)initiate the loading when the input set changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [iElementsWithCache],
  );

  // The promises for objects that are still being loaded
  const [loadPromises, setLoadPromises] = useState(() =>
    getLoadPromises(cacheEntries),
  );
  // The objects that have already finished loading
  const [loadedObjects, setLoadedObjects] = useState(() =>
    getLoadedObjects(cacheEntries),
  );

  // Update when the cache entries change
  useEffect(() => {
    // Only update the state if the cache entries have changed.
    // Not having this check would cause an infinite loop.
    const currentLoadedPromises = getLoadPromises(cacheEntries);
    if (hasChanged("loadPromises", currentLoadedPromises, shallowEqual)) {
      setLoadPromises(currentLoadedPromises);
    }

    // Only update the state if the loaded objects have changed.
    // Not having this check would cause an infinite loop.
    const currentLoadedObjects = getLoadedObjects(cacheEntries);
    if (hasChanged("loadedObjects", currentLoadedObjects, shallowEqual)) {
      setLoadedObjects(currentLoadedObjects);
    }
  }, [hasChanged, cacheEntries, loadPromises, loadedObjects]);

  // Update when a new object has been loaded
  useEffect(() => {
    if (!loadPromises.length) return;

    let cancelled = false;

    Promise.race(loadPromises).then(() => {
      if (cancelled) return;
      setLoadPromises(getLoadPromises(cacheEntries));
      setLoadedObjects(getLoadedObjects(cacheEntries));
    });

    return () => {
      cancelled = true;
    };
  }, [cacheEntries, loadPromises]);

  return loadedObjects;
}

/**
 * @param iElements The list of iElements we want to get the cache for
 * @param acquirer The acquirer to add and remove to the cache entries
 * @returns Elements with cache and the caches
 */
function useElementsWithCache<T extends SupportedElements>(
  iElements: T[] | undefined | null,
  acquirer: ObjectCacheAcquirer,
): {
  iElementsWithCache: T[];
  caches: Array<ObjectCache<T, IElementObjects[T["type"]]>>;
} {
  const { iElementsWithCache, caches } = useMemo(() => {
    const iElementsWithCache: T[] = [];
    const caches: Array<ObjectCache<T, IElementObjects[T["type"]]>> = [];
    if (!iElements) return { iElementsWithCache, caches };

    for (const iElement of iElements) {
      const cache = getCache(iElement);
      if (!cache) continue;
      iElementsWithCache.push(iElement);
      caches.push(cache);
    }
    return { iElementsWithCache, caches };
  }, [iElements]);

  useRenderOnCacheUpdatesForElements(caches, iElementsWithCache);
  useCacheRefs(caches, iElementsWithCache, acquirer);

  return { iElementsWithCache, caches };
}

/**
 * @param cacheEntries The cache entries to get the load promises for.
 * @returns The promises for the entries that still need to be loaded.
 */
function getLoadPromises<T extends SupportedElements>(
  cacheEntries: Array<CacheEntry<IElementObjects[T["type"]]>>,
): Array<Promise<IElementObjects[T["type"]]>> {
  return cacheEntries
    .map((entry) => (entry.response ? undefined : entry.promise))
    .filter(isValid);
}

/**
 * @param cacheEntries The cache entries to get the loaded objects for.
 * @returns The objects which are already loaded.
 */
function getLoadedObjects<T extends SupportedElements>(
  cacheEntries: Array<CacheEntry<IElementObjects[T["type"]]>>,
): Array<IElementObjects[T["type"]]> {
  return cacheEntries.map((entry) => entry.response).filter(isValid);
}

/**
 * Get an object from the cache, if not there return null and start preloading
 *
 * @param iElement The descriptor of the element
 * @param acquirerLabel Label of the acquirer to add and remove from the cache entries
 * @returns The object or null if the object is not ready
 */
export function useCached3DObjectIfReady<T extends SupportedElements>(
  iElement: T | undefined,
  acquirerLabel: string,
): IElementObjects[T["type"]] | Error | null {
  const acquirer = useObjectCacheAcquirer(acquirerLabel);

  const { tokenProvider } = useAuthContext();
  const { renderer } = useThreeContext();
  const modelLoadingCallback = useModelAboutToLoad();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();

  // Query object type metadata
  const cache = getCache(iElement);

  // Wrap the object to load in a state so we can re-draw when the object is ready
  const [object, setObject] = useState<
    IElementObjects[T["type"]] | null | Error
  >(null);

  useEffect(() => {
    setObject(null);

    if (!iElement) {
      return;
    }

    // Get or load the object
    const res = cache?.preload(iElement, {
      tokenProvider,
      modelLoadingCallback,
      getModelMaxByteToLoad: () =>
        // using a callback here because defaultMaximumModelSize will change AFTER cache?.preload has been
        getModelMaxByteToLoad(store.getState()),
      projectApi,
      pointCloudApiClient,
    });
    if (res?.response) {
      // If the object is ready notify to the component
      setObject(res.response);
    } else if (res?.error) {
      // If the object is not ready return null
      setObject(
        res.error instanceof Error
          ? res.error
          : new Error(JSON.stringify(res.error)),
      );
    } else {
      // And attach setObject to the loading promise
      res?.promise.then(setObject).catch(setObject);
    }
  }, [
    cache,
    tokenProvider,
    iElement,
    renderer,
    modelLoadingCallback,
    projectApi,
    pointCloudApiClient,
  ]);

  useRenderOnCacheUpdateForElement(cache, iElement?.id);
  useCacheRef(cache, iElement, acquirer);

  return object;
}

/**
 * Preload an object in the cache for later use
 *
 * @param iElement The descriptor of the element
 * @param params Optional parameters for the loading
 */
function preload3DObject<T extends SupportedElements>(
  iElement: T | undefined,
  params: LoaderFunctionParams,
): void {
  if (!iElement) return;
  // Get the cache for this object, inside this function the cache for typescript can be any of the available cache
  // and there's no way to have this code generic
  const cache = getCache(iElement);

  // @ts-expect-error We know here the cache is the correct one, but typescript can't infer that
  cache.preload(iElement, params);
}

/**
 * Hook to preload a 3d object as soon as a component is mounted and keep it in cache while the component is used
 *
 * @param iElement The element we want to preload
 * @param acquirerLabel Label of the acquirer to add and remove from the cache entries
 */
export function usePreload3DObject<T extends SupportedElements>(
  iElement: T | undefined,
  acquirerLabel: string,
): void {
  const acquirer = useObjectCacheAcquirer(acquirerLabel);

  const { tokenProvider } = useAuthContext();
  const { renderer } = useThreeContext();
  const modelLoadingCallback = useModelAboutToLoad();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();

  // Query object type metadata
  const cache = getCache(iElement);

  // Preload on mount, use layout effect to make sure this happen before the
  // hook in useCacheRef
  useEffect(() => {
    preload3DObject(iElement, {
      tokenProvider,
      modelLoadingCallback,
      getModelMaxByteToLoad: () => getModelMaxByteToLoad(store.getState()),
      projectApi,
      pointCloudApiClient,
    });
  }, [
    tokenProvider,
    iElement,
    renderer,
    modelLoadingCallback,
    projectApi,
    pointCloudApiClient,
  ]);

  useCacheRef(cache, iElement, acquirer);
}

/**
 * Hook to preload a list of 3d objects as soon as a component is mounted and keep it in cache while the component is used
 *
 * @param toPreload The list of elements we want to preload
 * @param acquirerLabel Label of the acquirer to add and remove from the cache entries
 * @returns a list of promises that load the objects.
 */
export function usePreload3DObjects<T extends SupportedElements>(
  toPreload: Array<T | undefined>,
  acquirerLabel: string,
): Array<MaybeCachePromiseOf<T>> {
  const acquirer = useObjectCacheAcquirer(acquirerLabel);

  const { tokenProvider } = useAuthContext();
  const modelLoadingCallback = useModelAboutToLoad();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();
  // The actual preload needs to be triggered in the render function, so it
  // executes before suspenses that block the effect below
  for (const element of toPreload) {
    if (!element) continue;
    preload3DObject(element, {
      tokenProvider,
      modelLoadingCallback,
      getModelMaxByteToLoad: () => getModelMaxByteToLoad(store.getState()),
      projectApi,
      pointCloudApiClient,
    });
  }

  // Register a reference to keep the objects loaded while the component is mounted
  useEffect(() => {
    const disposals: Array<() => void> = [];
    for (const element of toPreload) {
      if (!element) continue;
      const cache = getCache(element);
      if (cache) {
        cache.acquire(element, acquirer);
        disposals.push(() => cache.release(element, acquirer));
      }
    }
    return () => {
      for (const dispose of disposals) {
        dispose();
      }
    };
  }, [tokenProvider, toPreload, acquirer]);

  return useMemo(() => toPreload.map(getPromise), [toPreload]);
}

/**
 * Preloads the provided objects and suspends while they are loading.
 * Use when preloaded objects are nice to have, but not required.
 *
 * TODO: Add a fallback/timeout for slower internet connections
 * see https://faro01.atlassian.net/browse/SWEB-2043
 *
 * @param toPreload the objects to preload
 * @param acquirerLabel Label of the acquirer to add and remove from the cache entries
 */
export function useSuspendWhilePreload3DObjects(
  toPreload: SupportedElements[],
  acquirerLabel: string,
): void {
  const loadingPromises = usePreload3DObjects(toPreload, acquirerLabel);
  const states = useCached3DObjectsLoadingStates(toPreload);
  const isLoading = states.some(
    (state) => state.state === LoadingStates.loading,
  );

  if (isLoading) {
    throw Promise.all(loadingPromises);
  }
}

/** @returns a function to force a cache purge and reload of objects in the cache. */
export function useReload3DObjectInCache(): (
  element: SupportedElements,
) => Promise<void> {
  const { tokenProvider } = useAuthContext();
  const modelLoadingCallback = useModelAboutToLoad();
  const { projectApiClient: projectApi, pointCloudApiClient } =
    useApiClientContext();

  return async (element: SupportedElements) => {
    const cache = getCache(element);

    if (!cache) return;

    const entry = cache.reload(element, {
      tokenProvider,
      modelLoadingCallback,
      getModelMaxByteToLoad: () => getModelMaxByteToLoad(store.getState()),
      projectApi,
      pointCloudApiClient,
    });

    await entry?.promise;
  };
}

/**
 * Forces a re-render of the current component, when the iElement updates in the cache.
 * This hook is needed, whenever a non-reactive cache-entry value is returned as reactive state, as React
 * would otherwise miss changes to them.
 *
 * @param cache The cache holding the element
 * @param iElementId The element to listen for updates on
 */
function useRenderOnCacheUpdateForElement<T extends SupportedElements>(
  cache?: ObjectCache<T, IElementObjects[T["type"]]>,
  iElementId?: GUID,
): void {
  const forceRender = useForceRender();

  useEffect(() => {
    if (!cache || !iElementId) return;

    function onEntryUpdated({ element }: { element: T }): void {
      if (element.id === iElementId) {
        // Force a render, to emulate reactivity for the non-reactive cache values.
        forceRender();
      }
    }
    cache.onEntryUpdated.on(onEntryUpdated);
    return () => cache.onEntryUpdated.off(onEntryUpdated);
  }, [cache, iElementId, forceRender]);
}

/**
 * Forces a re-render of the current component, when one of the iElements provided updates in its cache.
 * This hook is needed, whenever a non-reactive cache-entry values are returned as reactive state, as React
 * would otherwise miss changes to them.
 *
 * @param caches The caches holding the elements
 * @param iElementsForCaches The iElements to listen for updates on
 */
function useRenderOnCacheUpdatesForElements<T extends SupportedElements>(
  caches: Array<ObjectCache<T, IElementObjects[T["type"]]>>,
  iElementsForCaches: T[],
): void {
  const forceRender = useForceRender();

  assert(
    caches.length === iElementsForCaches.length,
    "Caches and iElementsForCaches should have the same length",
  );

  useEffect(() => {
    const cleanupFns: Array<() => void> = [];

    for (let i = 0; i < caches.length; i++) {
      const cache = caches[i];
      const elementForCache = iElementsForCaches[i];
      function onEntryUpdated({ element }: { element: T }): void {
        if (element.id === elementForCache.id) {
          // Force a render, to emulate reactivity for the non-reactive cache values.
          forceRender();
        }
      }

      cache.onEntryUpdated.on(onEntryUpdated);

      cleanupFns.push(() => cache.onEntryUpdated.off(onEntryUpdated));
    }

    return () => {
      for (const unsubscribe of cleanupFns) {
        unsubscribe();
      }
    };
  }, [caches, forceRender, iElementsForCaches]);
}

/**
 * Possible loading states for a cached object
 */
export enum LoadingStates {
  /** Object is not in the cache, loading not even started */
  missing = "missing",

  /** Object is in the cache and is currently loading, data is not yet available */
  loading = "loading",

  /** Object is in the cache and loaded, data is available */
  ready = "ready",

  /** Object is in the cache but failed to load */
  error = "error",
}

/**
 * Type returned by useCached3DObjectLoadingState when the object is not in an error state
 */
export type ValidState = {
  /** Current loading state of the object */
  state: LoadingStates.missing | LoadingStates.loading | LoadingStates.ready;
};

/**
 * Type returned by useCached3DObjectLoadingState when the object is in an error state
 * with the error reason attached
 */
export type ErrorState = {
  /** Object error state  */
  state: LoadingStates.error;

  /** Reason of the error state */
  reason: unknown;
};

/** All possible states returned by useCached3DObjectLoadingState */
export type ObjectLoadingState = ValidState | ErrorState;

/**
 * Check the loading state for an element in a cache
 *
 * @param entry in the cache we want to check the loading state
 * @returns the loading state for the element in the cache
 */
function getObjectLoadingState(
  entry: Readonly<CacheEntry<Object3D>> | undefined,
): ObjectLoadingState {
  // A missing entry means the object was never requested and is not in cache
  if (!entry) {
    return { state: LoadingStates.missing };
  }

  // An existing entry error mark a failed load
  if (entry.error) {
    return {
      state: LoadingStates.error,
      reason: entry.error,
    };
  }

  // An existing response mark a successful load
  if (entry.response) {
    return {
      state: LoadingStates.ready,
    };
  }

  // If both are missing the loading is still pending
  return {
    state: LoadingStates.loading,
  };
}

/**
 * Query the loading state of an object
 *
 * @param iElement for which to query the current loading state
 * @returns the current loading state (missing, loading, ready or error)
 */
export function useCached3DObjectLoadingState<T extends SupportedElements>(
  iElement: T | undefined,
): ObjectLoadingState {
  return useCached3DObjectsLoadingStates<T>([iElement])[0];
}

/**
 * @returns the loading states for a set of iElements
 * @param iElements the iElements to check
 */
function getLoadingStates<T extends SupportedElements>(
  iElements: Array<T | undefined>,
): ObjectLoadingState[] {
  const states = [];

  for (const iElement of iElements) {
    const cache = getCache(iElement);
    const entry = iElement ? cache?.entry(iElement.id) : undefined;

    states.push(getObjectLoadingState(entry));
  }

  return states;
}

/**
 * Query the loading state of an array of objects
 *
 * @param iElements for which to query the current loading state
 * @returns the current loading state (missing, loading, ready or error)
 */
export function useCached3DObjectsLoadingStates<T extends SupportedElements>(
  iElements: Array<T | undefined>,
): ObjectLoadingState[] {
  // Set the initial loading state
  const [loadingStates, setLoadingStates] = useState<ObjectLoadingState[]>(() =>
    getLoadingStates(iElements),
  );

  // Use the elementIds to detect changes in IElements
  const elementIds = iElements.map((iElement) => iElement?.id);

  // React to a change of the tracked iElement
  // Should only update when an iElement's id changes
  useNonExhaustiveEffect(() => {
    function updateLoadingStates(): void {
      return setLoadingStates(getLoadingStates(iElements));
    }

    const disposables: Disposable[] = [];

    for (const iElement of iElements) {
      const cache = getCache(iElement);
      const entry = iElement ? cache?.entry(iElement.id) : undefined;

      if (!entry) {
        continue;
      }

      // Update state when promise finishes
      entry.promise.finally(() => {
        updateLoadingStates();
      });

      // Update state when the object is disposed
      disposables.push(
        entry.disposed.on(() => {
          updateLoadingStates();
        }),
      );
    }

    return () => {
      for (const disposable of disposables) {
        disposable.dispose();
      }
    };
  }, [...elementIds]);

  return loadingStates;
}

/**
 * @returns the value to be used for modelMaxByteToLoad
 * @param storeState state of the store
 */
function getModelMaxByteToLoad(storeState: RootState): number {
  const featureValue = selectGetFeatureValueIfEnabled(Features.LargeCadModel)(
    storeState,
  );
  return featureValue !== undefined && featureValue !== null
    ? +featureValue * 1024 * 1024
    : selectDefaultCadMaxByteToLoad()(storeState);
}

/**
 * @param iElement element that failed to load
 * @returns the message to display when the element failed to load
 */
function getErrorLoadingMessage(iElement: SupportedElements): string {
  if (iElement.type === IElementType.model3dStream) {
    return "Error loading 3D model";
  }
  if (iElement.type === IElementType.pointCloudStream) {
    return "Error loading point cloud";
  }
  if (iElement.type === IElementType.imgSheet) {
    return "Error loading sheet image";
  }
  if (iElement.type === IElementType.img360) {
    return "Error loading pano image";
  }
  if (iElement.type === IElementType.imgSheetTiled) {
    return "Error loading tiled sheet image";
  }
  return "Error loading element";
}

/**
 * This function checks that the cache entries for a determined data type match the expected size.
 * If this does not occur, all cached elements IDs and their acquirers are logged to console.
 *
 * @param elementType the type of element to check in the cache
 * @param expectedSize the expected number of cache entries for the given element type
 */
export function validateCacheSize(
  elementType: SupportedElements["type"],
  expectedSize: number,
): void {
  const cache = OBJECT_CACHES[elementType];

  const elementsAndAcquirers = cache.entriesAndAcquirers;

  if (elementsAndAcquirers.size > expectedSize) {
    console.warn(
      `Cache size for element type "${elementType}" is ${elementsAndAcquirers.size}, expected ${expectedSize}.`,
    );
    for (const [elementId, acquirers] of elementsAndAcquirers) {
      const acquirersString = acquirers
        .map((acquirer) => acquirer.label)
        .join(", ");
      console.warn("Element ", elementId, "Acquirers: ", acquirersString);
    }
  }
}

/**
 * This hook checks that the cache entries for a determined data type match the expected size.
 * If this does not occur, all cached elements IDs and their acquirers are logged to console.
 *
 * @param elementType the type of element to check in the cache
 * @param expectedSize the expected number of cache entries for the given element type
 */
export function useValidateCacheSize(
  elementType: SupportedElements["type"],
  expectedSize: number,
): void {
  useEffect(() => {
    validateCacheSize(elementType, expectedSize);
  });
}
